import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import './styles/fonts.css';
import './styles/typography.css';
import './styles/colors.css';
import './styles/global.css';

import React from 'react';
import { ComponentTypes } from '@dazn/lp-web/src/constants/componentTypes.constants';
import { usePageData } from '@dazn/lp-web/src/hooks/usePageData';
import Header from '@dazn/lp-web/src/components/Header/Header';
import BannerSlider from '@dazn/lp-web/src/components/BannerSlider/BannerSlider';
import ComingUpRail from '@dazn/lp-web/src/components/ComingUpRail/ComingUpRail';
import CompetitionCarousal from '@dazn/lp-web/src/components/CompetitionCarousal/CompetitionCarousal';
import FreemiumBanner from '@dazn/lp-web/src/components/FreemiumBanner/FreemiumBanner';
import Footer from '@dazn/lp-web/src/components/Footer/Footer';
import Faqs from '@dazn/lp-web/src/components/Faqs/Faqs';
import SubscriptionsRail from '@dazn/lp-web/src/components/SubscriptionsRail/SubscriptionsRail';
import StandardRail from '@dazn/lp-web/src/components/StandardRail/StandardRail';
import ContentTiers from '@dazn/lp-web/src/components/ContentTiers/ContentTiers';
import SpotlightRail from '@dazn/lp-web/src/components/SpotlightRail/SpotlightRail';
import SupportedDevices from '@dazn/lp-web/src/components/SupportedDevices/SupportedDevices';
import FeaturesV2 from '@dazn/lp-web/src/components/FeaturesV2/FeaturesV2';
import { usePreventPullToRefresh } from '@dazn/lp-web/src/hooks/usePreventPullToRefresh';
import {
    PageViewCategoryEnum,
    usePageViewTracking
} from '@dazn/lp-web/src/hooks/usePageViewTracking';
import Player from '@dazn/lp-web/src/components/VideoPlayer/VideoPlayer';
import Benefits from '@dazn/lp-web/src/components/Benefits/Benefits';
import DeviceGraphic from '@dazn/lp-web/src/components/DeviceGraphic/DeviceGraphic';
import ShowsRail from '@dazn/lp-web/src/components/ShowsRail/ShowsRail';
import { useOnInitialPageLoad } from '@dazn/lp-web/src/hooks/useOnInitialPageLoad';
import isQueryParamTrue from '@dazn/lp-web/src/utils/isQueryparamTrue';
import { Countdown } from '@dazn/lp-web/src/components/Countdown';
import { useYellowMessenger } from '@dazn/lp-web/src/utils/yellowMessangerChatbot';
import { TermsAndConditions } from '@dazn/lp-web/src/components/TermsAndConditions/TermsAndConditions';
import { useLandingPagesContext } from './contexts/LandingPageContext';
import { ErrorModal } from '@dazn/lp-web/src/components/ErrorModal/ErrorModal';
import { FullScreenBanner } from '@dazn/lp-web/src/components/PartnerBanner/PartnerBanner';
import { getHideComponents } from './components/Header/Utils/features';
import RedeemPage from './components/Redeem/RedeemPage';
import HeroBannerCenterAligned from '@dazn/lp-web/src/components/HeroBannerCenterAligned/HeroBannerCenterAligned';
import { CompetitionDisplay } from './components/CompetitionDisplay';
import ArticlesRail from '@dazn/lp-web/src/components/ArticlesRail/ArticlesRail';
import { UMSPopupIntraction } from '@dazn/lp-web/src/components/UMSPopupIntractions/UMSPopupIntraction';
import { MarketProposition } from './components/MarketProposition/MarketPraposition';
import { DaznBetBanner } from './components/Daznbet/DaznBet';

const UnifiedLandingPage: React.FC<{ root?: any }> = ({ root }) => {
    const {
        data,
        error,
        isLoading,
        pageError,
        hideExploreButton,
        hideHeader = false
    } = usePageData();
    const { showModal } = useLandingPagesContext();

    usePreventPullToRefresh();
    usePageViewTracking(PageViewCategoryEnum.LandingPage);
    useOnInitialPageLoad(root);
    useYellowMessenger();

    if (isLoading || error) return null;

    return (
        <div className="unified-wrapper">
            {hideHeader ? null : (
                <Header
                    hideExploreButton={hideExploreButton}
                    pageError={pageError}
                />
            )}
            <main className="main">
                {data?.map((data: any, index: number) => {
                    if (!getHideComponents()?.includes(data?.componentType)) {
                        return <>{render(data, index)}</>;
                    }
                    return null;
                })}
            </main>
            {showModal && <ErrorModal />}
        </div>
    );
};

function render(data: any, index: number): JSX.Element | null {
    switch (data.componentType) {
        case ComponentTypes.BANNERS:
            return (
                <BannerSlider
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.HEROBANNER_CENTER_ALIGNED:
            return (
                <HeroBannerCenterAligned
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.COMPETITION_CAROUSEL:
            return (
                <CompetitionCarousal
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.BENEFITS:
            return (
                <Benefits
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.DEVICEGRAPHIC:
            return (
                <DeviceGraphic
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                    data={data}
                    key={index}
                />
            );
        case ComponentTypes.FREEMIUM_BANNER:
            return (
                <FreemiumBanner
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                    componentPosition={index}
                />
            );
        case ComponentTypes.SPOTLIGHT_RAIL:
            return (
                <SpotlightRail
                    rail={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.CONTENT_TIERS:
            return (
                <ContentTiers
                    contentTiers={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.STANDARD_RAIL:
            return (
                <StandardRail
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.MARKET_PROPOSITION:
            return (
                <MarketProposition data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.SHOWS_RAIL:
            return (
                <ShowsRail
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.COMING_UP_RAIL:
            return (
                <ComingUpRail
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.SUBSCRIPTIONS_RAIL:
            return (
                <SubscriptionsRail
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.DAZN_BET:
            return (
                <DaznBetBanner
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            )
        case ComponentTypes.FAQS:
            return (
                <Faqs
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.FOOTER:
            return (
                <Footer
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.VIDEO_PLAYER:
            return <Player data={data} index={index} key={index} />;
        case ComponentTypes.COUNTDOWN:
            return <Countdown data={data} key={index} />;
        case ComponentTypes.TERMSANDCONDITIONS:
            return <TermsAndConditions data={data} key={index} />;
        case ComponentTypes.FULLSCREEN_BANNER:
            return <FullScreenBanner data={data} key={index} />;

        case ComponentTypes.FEATURES_V2:
            return (
                <FeaturesV2
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );

        case ComponentTypes.SUPPORTED_DEVICES:
            return (
                <SupportedDevices
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );

        case ComponentTypes.REDEEM_PAGE:
            return (
                <RedeemPage
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.COMPETITION_DISPLAY:
            return (
                <CompetitionDisplay
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.ARTICLES_RAIL:
            return (
                <ArticlesRail
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        case ComponentTypes.UMS_POPUP_INTRACTION:
            return (
                <UMSPopupIntraction
                    data={data}
                    key={index}
                    disableAnimation={index === 1 || isQueryParamTrue('seo')}
                />
            );
        default:
            return null;
    }
}

export default UnifiedLandingPage;
